import { Component, OnInit, Input } from '@angular/core';
import { CustomInputTypesDefinitionsService } from '../../services/configuration/custom-input-types-definitions.service';
import { PCDBLOOKUPS } from '../../constants/pcdb-lookups.constant';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PcdbSearchService } from '../../services/configuration/rdsap-pcdb-search.service';
import { rdsapPCDBData } from '../../config/rdsap-pcdb-data.config';

const ModelKey = 'modelName';
const BrandKey = 'brandName';
const ModelPlaceholderText = 'Select a model';
const BrandPlaceholderText = 'Select a brand';

const fuelCodeMap = { //this.heatingFuel: fuelCode in pcdb
  "3": 2, "5": 15, "6": 20, "7": 23, "8": 21, "9": 10, "15": 12, "16": 22, "17": 2, "18": 75, "19": 76, "20": 51,
  "21": 52, "22": 53, "23": 55, "24": 54, "26": 1, "27": 2, "28": 4, "29": 39, "30": 42, "31": 43, "32": 44, "33": 11,
  "34": 71, "35": 72, "36": 73, "37": 74, "38": 45, "39": 46
}

@Component({
  selector: 'app-pcdb-data-selector',
  templateUrl: './pcdb-data-selector.component.html',
  styleUrls: ['./pcdb-data-selector.component.scss'],
})
export class PcdbDataSelectorComponent implements OnInit {
  @Input() heatingCategoryOptions: any[];
  @Input() heatingCategory: any;
  @Input() heatingFuel: any;
  @Input() heatingFuelOptions: any[];
  @Input() keyedLookUpCodes: any;
  @Input() communityHeatingFuelCodes: any[];
  @Input() mainGasAvailable: boolean;
  @Input() inputTypeDefinitions: any;

  isLoading = false;
  pcdbDataResults: any[] = [];
  pcdbSearchResults: any[] = [];
  selectedPcdbData: any;
  heatingFuels: any[];
  heatingCategories: any[];
  searchData: any; // The current search data
  searchDataBackup: any; // Backup of search data for comparison
  heatingCategoryBackup: any; // Backup of heating category
  heatingFuelBackup: any; // Backup of heating fuel
  pcdbSearchResultsBackup: any[]; // Backup of search results
  uniqueBrandNames: Set<string> = new Set<string>();
  uniqueModelNames: Set<string> = new Set<string>();
  brandNameOptions: { id: string; name: string }[] = [];
  modelNameOptions: { id: string; name: string }[] = [];
  fuelType: any;

  pcdbLookUps = PCDBLOOKUPS;

  dtOptions = {
    dom: '<"top"f>rt<"bottom"<"left"<"length"l>><"right"<"info"i><"pagination"p>>>',
    pagingType: 'simple',
    autoWidth: true,
    responsive: true,
    order: [0, 'asc'],
  };

  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  filteredModelNameOptions: { id: string; name: string; }[];
  field1: string;
  field2: string;

  constructor(
    public activeModal: NgbActiveModal,
    private customInputTypesDefinitionsService: CustomInputTypesDefinitionsService,
    private pcdbSearchService: PcdbSearchService
  ) { }

  selectedBrandNameFromBrandChanged = '';
  selectedModelNameFromModelChanged = '';
  private pcdbData = rdsapPCDBData;

  initializeUniqueBrandNames(): void {
    const uniqueBrandNamesArray = this.getUniqueNames(BrandKey);
    const uniqueModelNamesArray = this.getUniqueNames(ModelKey);
    this.uniqueBrandNames = new Set(
      uniqueBrandNamesArray.map((item) => item.name)
    );
    this.uniqueModelNames = new Set(
      uniqueModelNamesArray.map((item) => item.name)
    );
    this.brandNameOptions = uniqueBrandNamesArray;
    this.modelNameOptions = uniqueModelNamesArray;
  }

  ngOnInit(): void {
    this.heatingCategories = this.heatingCategoryOptions.map((category) => {
      return {
        id: category.lookupValue,
        name: category.lookUpItem,
      };
    });

    this.customInputTypesDefinitionsService
      .getDefinitions()
      .subscribe((values) => {
        this.inputTypeDefinitions = values;
        this.initializeUniqueBrandNames();
      });
  }

  setSelectedPcdbData(selectedPcdbData) {
    this.selectedPcdbData = selectedPcdbData;
  }

  brandChanged(selectedBrandId: string, forceReset = false): void {
    if (forceReset) {
      this.selectedModelNameFromModelChanged = '';
    }
    const brandName = this.brandNameOptions.find(
      (option) => option.id === selectedBrandId
    )?.name;

    if (brandName) {
      if (this.selectedBrandNameFromBrandChanged === brandName && !forceReset) {
        this.selectedBrandNameFromBrandChanged = '';
        this.uniqueBrandNames = new Set(
          this.brandNameOptions.map((item) => item.name)
        );
        this.modelNameOptions = this.getUniqueNames(ModelKey);
      } else {
        this.selectedBrandNameFromBrandChanged = brandName;
        this.uniqueBrandNames = new Set([brandName]);
        this.modelNameOptions = this.getUniqueNames(ModelKey, brandName);
      }

      this.loadPCDBData();
    }
  }

  modelChanged(selectedModelId: string): void {
    if (this.selectedBrandNameFromBrandChanged && selectedModelId === '') {
      const brandId = this.brandNameOptions.find(
        (option) => option.name === this.selectedBrandNameFromBrandChanged
      )?.id;
      this.brandChanged(brandId, true);

      return;
    }
    const modelName = this.modelNameOptions.find(
      (option) => option.id === selectedModelId
    )?.name;

    if (modelName) {
      if (this.selectedModelNameFromModelChanged === modelName) {
        this.selectedModelNameFromModelChanged = '';
        this.uniqueModelNames = new Set(
          this.modelNameOptions.map((item) => item.name)
        );
      } else {
        this.selectedModelNameFromModelChanged = modelName;
        this.uniqueModelNames = new Set([modelName]);
      }

      this.loadPCDBData();
    }
  }

  getUniqueNames(
    propertyName: string,
    selectedName?: string
  ): { id: string; name: string }[] {
    const uniqueNamesMap: Map<string, { id: string; name: string }> = new Map();
    const keysWithValue = Object.keys(fuelCodeMap).filter(key => fuelCodeMap[key] === this.heatingFuel);

    this.pcdbData.data.pcdf.forEach((item) => {
      if (item[propertyName]) {
        if (!selectedName || item.brandName === selectedName) {
          let newItem: { id: string; name: string; fuel: string[] };

          if (item.type === 'HeatPump' && fuelCodeMap[this.heatingFuel] == item.field2) {
            newItem = { id: item.id, name: item[propertyName], fuel: [item.field2] };
          } else if (fuelCodeMap[this.heatingFuel] == item.field1) {
            newItem = { id: item.id, name: item[propertyName], fuel: [item.field1] };
          }

          if (newItem) {
            if (keysWithValue.includes(this.heatingFuel.toString())) {
              newItem.fuel.push(this.heatingFuel.toString());
            }

            uniqueNamesMap.set(newItem.name, newItem);
          }
        }
      }
    });

    const uniqueNamesArray = Array.from(uniqueNamesMap.values());

    uniqueNamesArray.sort((a, b) => a.name.localeCompare(b.name));

    return [{ id: '', name: propertyName == ModelKey ? ModelPlaceholderText : BrandPlaceholderText }, ...uniqueNamesArray];
  }

  searchDataChanged(data: string): void {
    this.searchData = data;
  }

  cancel(): void {
    this.activeModal.close();
  }

  loadPCDBData() {
    this.heatingCategoryBackup = { ...this.heatingCategory };
    this.heatingFuelBackup = { ...this.heatingFuel };
    this.searchDataBackup = { ...this.searchData };

    this.isLoading = true;

    const selectedBrandName = this.selectedBrandNameFromBrandChanged;
    const selectedModelName = this.selectedModelNameFromModelChanged;
    const searchInput = {
      brandName: selectedBrandName,
      modelName: selectedModelName,
      searchData: this.searchData || '',
      fuel: fuelCodeMap[this.heatingFuel] || []
    };

    this.pcdbSearchResults = this.pcdbSearchService.searchPcdbData(searchInput);

    this.totalItems = this.pcdbSearchResults.length;

    this.currentPage = 1;

    this.getPaginatedResults();

    this.isLoading = false;
  }

  getPaginatedResults(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(
      startIndex + this.itemsPerPage,
      this.pcdbSearchResults.length
    );
    return this.pcdbSearchResults.slice(startIndex, endIndex);
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getPaginatedResults();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.getPaginatedResults();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.getPaginatedResults();
    }
  }

  get totalPages(): number {
    return Math.ceil(this.pcdbSearchResults.length / this.itemsPerPage);
  }

  resetPcdbSearchResults(): void {
    if (
      this.heatingCategoryBackup !== this.heatingCategory ||
      this.heatingFuelBackup !== this.heatingFuel ||
      this.searchDataBackup !== this.searchData
    ) {
      this.pcdbSearchResults = [];
    } else {
      this.pcdbSearchResults = [...this.pcdbSearchResultsBackup];
    }
  }

  commitAndCloseModal(): void {
    this.activeModal.close({
      pcdbData: this.selectedPcdbData,
      category: this.heatingCategory,
      fuel: this.heatingFuel,
    });
  }

  getMainTypeDisplay(mainTypeValue: string): string {
    if (this.heatingCategory === '4' || this.heatingCategory === '5') {
      // if Heat pumps
      return this.pcdbLookUps.mainTypes.heatPump[mainTypeValue];
    }

    if (this.heatingCategory === '7') {
      // if electric storage
      return this.pcdbLookUps.mainTypes.electricStorageHeaters[mainTypeValue];
    }

    if (this.heatingCategory === '9') {
      // if warm air
      return this.pcdbLookUps.mainTypes.warmAir[mainTypeValue];
    }

    if (this.heatingCategory === '2') {
      // if gas or oil fuels
      if (
        [
          '26',
          '27',
          '3',
          '28',
          '37',
          '17',
          '18',
          '35',
          '36',
          '51',
          '19',
        ].includes(this.heatingFuel)
      ) {
        return this.pcdbLookUps.mainTypes.gasOilBoiler[mainTypeValue];
      }

      // if solid fuels
      if (
        ['33', '34', '5', '6', '7', '8', '9', '15'].includes(this.heatingFuel)
      ) {
        return this.pcdbLookUps.mainTypes.solidFuelBoiler[mainTypeValue];
      }
    }

    return mainTypeValue;
  }

  getFlueTypeDisplay(flueTypeValue: string): string {
    if (this.heatingCategory === '4' || this.heatingCategory === '5') {
      // if Heat pumps
      return this.pcdbLookUps['flueTypes']['heatPump'][flueTypeValue];
    } else {
      return this.pcdbLookUps['flueTypes']['default'][flueTypeValue];
    }
  }


    openPcdbRecord(data: any): void {
      // here, if no data available --> do nothing
      if (!data || !data.id) {
        return;
      }
  
      const indexId = data.id;
      let typeParam = '';
  
      // here, add 362 parameter if heatPump or 105 if boiler
      if (data.type === 'HeatPump') {
        typeParam = '362'; // Heat Pump
      } else {
        typeParam = '105'; // Boiler
      }
      // here, url with dynamic parameters
      const url = `https://www.ncm-pcdb.org.uk/sap/pcdbdetails.jsp?id=${indexId}&type=${typeParam}`;
  
      // here, open generated url on a new tab when the button clicked
      window.open(url, '_blank');
    }
  
}
